import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, setDoc, deleteDoc } from "firebase/firestore";
import { TableSortLabel } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../context/EnvContext";
import slugify from "slugify";
import config from "../../config";

const Categories = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryUrl, setCategoryUrl] = useState("");
  const [featuredProductTop, setFeaturedProductTop] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "product_name",
    direction: "asc",
  });

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [db, collectionPath]);

  const fetchCategories = async () => {
    const categoriesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/Categories/DataBase`);
    const querySnapshot = await getDocs(categoriesCollectionRef);
    const categoriesArray = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setCategories(categoriesArray);
  };

  const fetchProducts = async () => {
    const productsCollectionRef = collection(db, `${collectionPath}Listings/ListingsList/DataBase`);
    const querySnapshot = await getDocs(productsCollectionRef);
    const productsArray = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setProducts(productsArray);
  };

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setCategoryUrl(category.url ? category.url : `${slugify(category.name, { lower: true, strict: true })}`);
    setFeaturedProductTop(category.featuredProductTop || "");

    // Fetch updated product list and ensure selected products are marked
    const productsCollectionRef = collection(db, `${collectionPath}Listings/ListingsList/DataBase`);
    const querySnapshot = await getDocs(productsCollectionRef);
    const productsArray = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    setProducts(productsArray);
    setSelectedProducts(category.products || []);
  };

  const handleCheckboxChange = (productId) => {
    setSelectedProducts((prev) => (prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]));
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }

    const slug = slugify(categoryName, { lower: true, strict: true });
    const categoryUrlGenerated = `${slug}`;

    if (selectedCategory && selectedCategory.id) {
      const categoryRef = doc(db, `${collectionPath}SooperstockSettings/Categories/DataBase`, selectedCategory.id);
      await updateDoc(categoryRef, { name: categoryName, slug, url: categoryUrlGenerated, products: selectedProducts, featuredProductTop });
      toast.success("Category updated successfully");
    } else {
      const newCategoryRef = await addDoc(collection(db, `${collectionPath}SooperstockSettings/Categories/DataBase`), {
        name: categoryName,
        slug,
        url: categoryUrlGenerated,
        products: selectedProducts,
      });
      setSelectedCategory({ id: newCategoryRef.id, name: categoryName, url: categoryUrlGenerated, products: selectedProducts }); // Ensure it has an ID now
      toast.success("Category created successfully");
    }

    setCategoryUrl(categoryUrlGenerated);
    setSelectedCategory(null);
    setCategoryName("");
    setFeaturedProductTop("");
    setSelectedProducts([]);
    fetchCategories();
  };

  const handleSortRequest = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === "asc";
    setSortConfig({ key: property, direction: isAsc ? "desc" : "asc" });
  };

  const sortedProducts = [...products].sort((a, b) => {
    const order = sortConfig.direction === "asc" ? 1 : -1;
    const aValue = a[sortConfig.key] || "";
    const bValue = b[sortConfig.key] || "";

    return aValue.toString().localeCompare(bValue.toString()) * order;
  });

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
      {!selectedCategory ? (
        <div style={{ ...styles.form, padding: 10 }}>
          <h2>{environment === "staging" && <span style={{ background: "#dae121", marginRight: 5 }}>Staging</span>}Categories</h2>
          <button
            onClick={() => {
              setSelectedCategory({});
              setCategoryName("");
              setCategoryUrl("");
              setFeaturedProductTop("");
              setSelectedProducts([]);
            }}
            style={styles.newCategoryButton}
          >
            New Category
          </button>

          <table style={styles.table}>
            <thead>
              <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }}>
                <th style={styles.th}>Category Name</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id} onClick={() => handleCategoryClick(category)} style={styles.categoryRow}>
                  <td style={styles.td}>{category.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div>
          <h2>{selectedCategory.id ? "Edit Category" : "New Category"}</h2>
          <div onClick={() => setSelectedCategory(null)} style={{ marginBottom: 20, cursor: "pointer" }}>
            {"<"} Back to categories
          </div>
          <div style={{ padding: "10px", display: "flex", flexDirection: "column", marginBottom: 20 }}>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Custom category name:</label>
              <input type="text" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} placeholder="Category Name" style={styles.input} />
            </div>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Custom category url:</label>
              <input
                type="text"
                value={categoryUrl}
                readOnly
                placeholder="Category URL"
                style={{ ...styles.input, cursor: "pointer" }}
                onClick={() => {
                  navigator.clipboard.writeText(categoryUrl);
                  toast.success("URL copied to clipboard!");
                }}
              />
              <label style={{ ...styles.formLabel, padding: 0, fontSize: 10 }}>*click to copy in clipboard</label>
            </div>
            <div style={styles.formElement}>
              <label style={styles.formLabel}>Featured products TOP:</label>
              <input
                type="text"
                value={featuredProductTop}
                onChange={(e) => setFeaturedProductTop(e.target.value)}
                placeholder="Featured products TOP"
                style={styles.input}
              />
            </div>
          </div>
          <h3>Select Products</h3>
          <table style={styles.table}>
            <thead>
              <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }}>
                <th style={{ ...styles.th, width: "50px", textAlign: "center" }}>Select</th>
                <th style={styles.th}>
                  <TableSortLabel
                    active={sortConfig.key === "product_name"}
                    direction={sortConfig.key === "product_name" ? sortConfig.direction : "asc"}
                    onClick={() => handleSortRequest("product_name")}
                  >
                    Product Name
                  </TableSortLabel>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map((product) => (
                <tr key={product.id} style={{ ...styles.clickableRow, borderBottom: "1px solid #ccc" }}>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" checked={selectedProducts.includes(product.id)} onChange={() => handleCheckboxChange(product.id)} />
                  </td>
                  <td style={{ padding: "10px", fontSize: "14px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }}>{product.product_name}</span>
                      <span style={{ fontSize: 10, color: "gray" }}>UID# {`P${product.id}`}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: "flex", marginTop: 10 }}>
            <button onClick={handleSaveCategory} style={{ ...styles.button, marginRight: 10 }}>
              Save
            </button>
            <button onClick={() => setSelectedCategory(null)} style={{ ...styles.button, background: "#eaeced", color: "#333" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  table: { borderCollapse: "collapse", width: "100%" },
  th: { padding: "8px", textAlign: "left", borderBottom: "1px solid #ccc" },
  td: { padding: "8px", borderBottom: "1px solid #ccc", cursor: "pointer" },
  newCategoryButton: {
    padding: "10px",
    backgroundColor: "#478169",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    float: "right",
    marginBottom: "10px",
  },
  input: { padding: "10px", display: "flex", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "4px" },
  productGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "10px" },
  productItem: { width: "110px", padding: "5px", cursor: "pointer", textAlign: "center" },
  productImage: { width: "100px", height: "100px", objectFit: "cover" },
  button: {
    padding: "0px",
    width: "120px",
    backgroundColor: "#478169",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: "14px",
    height: "50px",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    marginBottom: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
};

export default Categories;
